import {
  queryOptions,
  useMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import { z } from 'zod'
import { api } from './client'

/**
 * Loops domain — list, get, create, update, delete, pause, resume.
 *
 * Backend canonical labels (see backend/internal/loopcv/enums.go):
 *   jobType  : full-time | contract | part-time | temporary | internship
 *   jobLevel : junior | associate | senior | executive | all
 *
 * Remote/hybrid is a mutually exclusive pair; we expose a single
 * 'workMode' triple-radio in the UI and translate to the booleans here.
 */

export const JOB_TYPES = [
  { value: 'full-time', label: 'Full-time' },
  { value: 'contract', label: 'Contract' },
  { value: 'part-time', label: 'Part-time' },
  { value: 'temporary', label: 'Temporary' },
  { value: 'internship', label: 'Internship' },
] as const

export const JOB_LEVELS = [
  { value: 'junior', label: 'Junior' },
  { value: 'associate', label: 'Associate' },
  { value: 'senior', label: 'Senior' },
  { value: 'executive', label: 'Executive' },
  { value: 'all', label: 'All levels' },
] as const

export const JOB_MATCH_LEVELS = [
  { value: 25, label: 'Loose (25%)' },
  { value: 50, label: 'Balanced (50%)' },
  { value: 75, label: 'Strict (75%)' },
] as const

export const WORK_MODES = [
  { value: 'any', label: 'Any (onsite + hybrid + remote)' },
  { value: 'hybrid', label: 'Include hybrid' },
  { value: 'remote', label: 'Remote only' },
] as const
type WorkMode = (typeof WORK_MODES)[number]['value']

const jobTypeSchema = z.enum(['', 'full-time', 'contract', 'part-time', 'temporary', 'internship'])
const jobLevelSchema = z.enum(['', 'junior', 'associate', 'senior', 'executive', 'all'])

/**
 * Per-loop counters surfaced by `GET /api/loops` (backend wraps LoopCV's
 * five totals into a `stats` substruct — see backend/internal/loopcv/types.go).
 *
 * The list endpoint always populates these; the detail endpoint may not.
 * Absent fields decode to 0, so callers should treat 0 as "not known"
 * rather than "definitely zero". The dashboard sums these across loops
 * to avoid paginating every match for KPI math.
 *
 * Semantic gotcha on `totalMatchesWithApplications`: upstream counts
 * matches with an *apply method available*, NOT matches actually applied.
 * That makes it the wrong field for an "Applied" KPI — we read the
 * authoritative applied count via a server-side filtered match query
 * instead (see appliedCountQuery below).
 */
export const loopStatsSchema = z.object({
  totalJobs: z.number().int(),
  totalMatches: z.number().int(),
  totalMatchesWithEmails: z.number().int(),
  totalMatchesWithApplications: z.number().int(),
  totalMatchesWithPendingQuestions: z.number().int(),
})

/** Server-side loop shape (canonical). */
export const loopSchema = z.object({
  id: z.string(),
  jobTitle: z.string(),
  jobLocation: z.string(),
  jobType: jobTypeSchema,
  jobLevel: jobLevelSchema,
  keywords: z.array(z.string()),
  excludedKeywords: z.array(z.string()),
  eliminatedCompanies: z.array(z.string()),
  scrapers: z.array(z.string()),
  jobMatchLevel: z.number().int(),
  onlyRemoteJobs: z.boolean(),
  hybridJobs: z.boolean(),
  enableFormApplications: z.boolean(),
  directEmails: z.boolean(),
  salaryMin: z.number().int().nullable(),
  salaryMax: z.number().int().nullable(),
  currency: z.string(),
  cvId: z.string(),
  hasCV: z.boolean(),
  paused: z.boolean(),
  pausedAt: z.string().nullable().optional(),
  // Default to a zeroed stats block so old cached responses (pre-18220ef)
  // don't blow up the schema parse during the deploy crossover.
  stats: loopStatsSchema.default({
    totalJobs: 0,
    totalMatches: 0,
    totalMatchesWithEmails: 0,
    totalMatchesWithApplications: 0,
    totalMatchesWithPendingQuestions: 0,
  }),
})
export type Loop = z.infer<typeof loopSchema>

export const loopPageSchema = z.object({
  items: z.array(loopSchema),
  total: z.number(),
  page: z.number(),
  perPage: z.number(),
})
export type LoopPage = z.infer<typeof loopPageSchema>

/** What the frontend sends on create/update (server validates again). */
export const loopInputSchema = z.object({
  jobTitle: z.string().min(1, 'Required'),
  jobLocation: z
    .string()
    .trim()
    .min(1, 'Required')
    .refine((value) => {
      const parts = value.split(',')
      return parts.length <= 2 && parts.every((part) => part.trim().length > 0)
    }, 'Choose a suggested city or use City, Country (without a state or region).'),
  jobType: jobTypeSchema.default(''),
  jobLevel: jobLevelSchema.default(''),
  keywords: z.array(z.string()).default([]),
  excludedKeywords: z.array(z.string()).default([]),
  eliminatedCompanies: z.array(z.string()).default([]),
  scrapers: z.array(z.string()).min(1, 'Pick at least one scraper'),
  jobMatchLevel: z.union([z.literal(25), z.literal(50), z.literal(75)]),
  workMode: z.enum(['any', 'hybrid', 'remote']),
  salaryMin: z.number().int().min(0).nullable(),
  salaryMax: z.number().int().min(0).nullable(),
  currency: z.string().default('SGD'),
  cvId: z.string().min(1, 'Pick a CV'),
  /**
   * When true, LoopCV's dameron bot will auto-apply on dameron-eligible
   * matches as they're scraped. When false, the user must explicitly
   * click "Apply with AI" per match. Defaults to false (safer).
   */
  enableFormApplications: z.boolean().default(false),
})
export type LoopInputForm = z.infer<typeof loopInputSchema>

/** Translate form → backend body. */
export function toLoopInputBody(input: LoopInputForm) {
  return {
    jobTitle: input.jobTitle,
    jobLocation: input.jobLocation,
    jobType: input.jobType,
    jobLevel: input.jobLevel,
    keywords: input.keywords,
    excludedKeywords: input.excludedKeywords,
    eliminatedCompanies: input.eliminatedCompanies,
    scrapers: input.scrapers,
    jobMatchLevel: input.jobMatchLevel,
    onlyRemoteJobs: input.workMode === 'remote',
    hybridJobs: input.workMode === 'hybrid',
    enableFormApplications: input.enableFormApplications,
    salaryMin: input.salaryMin,
    salaryMax: input.salaryMax,
    currency: input.currency,
    cvId: input.cvId,
  }
}

/** Translate a stored Loop into a form-shape (for editing). */
export function loopToFormDefaults(loop: Loop): LoopInputForm {
  const workMode: WorkMode = loop.onlyRemoteJobs ? 'remote' : loop.hybridJobs ? 'hybrid' : 'any'
  const matchLevel = loop.jobMatchLevel === 25 || loop.jobMatchLevel === 50 || loop.jobMatchLevel === 75
    ? loop.jobMatchLevel
    : 50
  return {
    jobTitle: loop.jobTitle,
    jobLocation: loop.jobLocation,
    jobType: loop.jobType,
    jobLevel: loop.jobLevel,
    keywords: loop.keywords,
    excludedKeywords: loop.excludedKeywords,
    eliminatedCompanies: loop.eliminatedCompanies,
    scrapers: loop.scrapers,
    jobMatchLevel: matchLevel,
    workMode,
    salaryMin: loop.salaryMin,
    salaryMax: loop.salaryMax,
    currency: loop.currency || 'SGD',
    cvId: loop.cvId,
    enableFormApplications: loop.enableFormApplications,
  }
}

/* -------------------------------------------------------------------------
 * Query / mutation factories
 * ------------------------------------------------------------------------- */

export const loopsKeys = {
  all: ['loops'] as const,
  list: (page: number, perPage: number) => [...loopsKeys.all, 'list', { page, perPage }] as const,
  detail: (id: string) => [...loopsKeys.all, 'detail', id] as const,
}

export const loopsListQuery = (page = 1, perPage = 50) =>
  queryOptions({
    queryKey: loopsKeys.list(page, perPage),
    queryFn: () => api.get<LoopPage>(`/loops?page=${page}&perPage=${perPage}`),
  })

export function useCreateLoopMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: LoopInputForm) =>
      api.post<{ id: string }>('/loops', toLoopInputBody(input)),
    onSuccess: () => invalidateLoops(qc),
  })
}

export function useUpdateLoopMutation(loopId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: LoopInputForm) =>
      api.put<void>(`/loops/${loopId}`, toLoopInputBody(input)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: loopsKeys.detail(loopId) })
      void invalidateLoops(qc)
    },
  })
}

export function useDeleteLoopMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (loopId: string) => api.delete<void>(`/loops/${loopId}`),
    onSuccess: () => invalidateLoops(qc),
  })
}

export function usePauseLoopMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (loopId: string) => api.post<void>(`/loops/${loopId}/pause`),
    onSuccess: (_d, loopId) => {
      void qc.invalidateQueries({ queryKey: loopsKeys.detail(loopId) })
      void invalidateLoops(qc)
    },
  })
}

export function useResumeLoopMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (loopId: string) => api.post<void>(`/loops/${loopId}/resume`),
    onSuccess: (_d, loopId) => {
      void qc.invalidateQueries({ queryKey: loopsKeys.detail(loopId) })
      void invalidateLoops(qc)
    },
  })
}

function invalidateLoops(qc: QueryClient) {
  return qc.invalidateQueries({ queryKey: loopsKeys.all })
}
